/**
 * Organic Nature Theme Styles
 * 
 * Estilos específicos para o tema Natureza Orgânica.
 * Características: formas orgânicas, cores terrosas, texturas naturais, animações suaves.
 */

import { CSSProperties } from 'react';

// ============================================
// BASE STYLES
// ============================================

export const organicNatureBase: CSSProperties = {
  background: 'var(--org-bg)',
  color: 'var(--org-text)',
  fontFamily: 'var(--org-font)',
  borderRadius: 'var(--org-radius)',
  boxShadow: 'var(--org-shadow)',
  transition: 'var(--org-transition)',
};

// ============================================
// INPUT STYLES
// ============================================

export const organicNatureInput: CSSProperties = {
  background: 'var(--org-bg-card)',
  color: 'var(--org-text)',
  border: '2px solid var(--org-primary)',
  borderRadius: 'var(--org-radius-sm)',
  padding: '14px 18px',
  fontSize: '15px',
  fontFamily: 'var(--org-font)',
  boxShadow: 'var(--org-shadow-sm)',
  transition: 'var(--org-transition)',
  outline: 'none',
};

export const organicNatureInputFocus: CSSProperties = {
  borderColor: 'var(--org-primary-light)',
  boxShadow: 'var(--org-shadow), 0 0 0 3px rgba(74, 124, 89, 0.2)',
};

export const organicNatureInputError: CSSProperties = {
  borderColor: 'var(--org-danger)',
  boxShadow: '0 0 0 3px rgba(166, 93, 87, 0.2)',
};

export const organicNatureInputDisabled: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
  background: 'var(--org-bg-secondary)',
};

// ============================================
// BUTTON STYLES
// ============================================

export const organicNatureButton: CSSProperties = {
  background: 'var(--org-primary)',
  color: '#ffffff',
  border: 'none',
  borderRadius: 'var(--org-radius)',
  padding: '14px 28px',
  fontSize: '15px',
  fontFamily: 'var(--org-font)',
  fontWeight: 'var(--org-font-weight-bold)' as unknown as number,
  boxShadow: 'var(--org-shadow)',
  cursor: 'pointer',
  transition: 'var(--org-transition)',
  outline: 'none',
};

export const organicNatureButtonHover: CSSProperties = {
  background: 'var(--org-primary-light)',
  boxShadow: 'var(--org-shadow-hover)',
  transform: 'translateY(-2px)',
};

export const organicNatureButtonActive: CSSProperties = {
  background: 'var(--org-primary-dark)',
  transform: 'translateY(0)',
  boxShadow: 'var(--org-shadow-sm)',
};

export const organicNatureButtonSecondary: CSSProperties = {
  background: 'var(--org-secondary)',
  color: '#ffffff',
};

export const organicNatureButtonGhost: CSSProperties = {
  background: 'transparent',
  color: 'var(--org-primary)',
  border: '2px solid var(--org-primary)',
  boxShadow: 'none',
};

export const organicNatureButtonDanger: CSSProperties = {
  background: 'var(--org-danger)',
  color: '#ffffff',
};

export const organicNatureButtonDisabled: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  transform: 'none',
};

// ============================================
// CARD STYLES
// ============================================

export const organicNatureCard: CSSProperties = {
  background: 'var(--org-bg-card)',
  color: 'var(--org-text)',
  borderRadius: 'var(--org-radius-lg)',
  boxShadow: 'var(--org-shadow)',
  padding: 'var(--org-spacing-lg)',
  transition: 'var(--org-transition)',
  border: '1px solid rgba(74, 124, 89, 0.1)',
};

export const organicNatureCardHover: CSSProperties = {
  boxShadow: 'var(--org-shadow-hover)',
  transform: 'translateY(-4px)',
};

export const organicNatureCardHeader: CSSProperties = {
  borderBottom: '1px solid rgba(74, 124, 89, 0.15)',
  paddingBottom: 'var(--org-spacing-md)',
  marginBottom: 'var(--org-spacing-md)',
};

export const organicNatureCardTitle: CSSProperties = {
  fontFamily: 'var(--org-font-display)',
  fontSize: '1.25rem',
  color: 'var(--org-primary-dark)',
};

export const organicNatureCardWithTexture: CSSProperties = {
  ...organicNatureCard,
  backgroundImage: 'var(--org-texture)',
  backgroundBlendMode: 'overlay',
};

// ============================================
// SELECT STYLES
// ============================================

export const organicNatureSelect: CSSProperties = {
  background: 'var(--org-bg-card)',
  color: 'var(--org-text)',
  border: '2px solid var(--org-primary)',
  borderRadius: 'var(--org-radius-sm)',
  padding: '14px 44px 14px 18px',
  fontSize: '15px',
  fontFamily: 'var(--org-font)',
  boxShadow: 'var(--org-shadow-sm)',
  cursor: 'pointer',
  transition: 'var(--org-transition)',
  appearance: 'none',
};

export const organicNatureSelectDropdown: CSSProperties = {
  background: 'var(--org-bg-card)',
  borderRadius: 'var(--org-radius)',
  boxShadow: 'var(--org-shadow-lg)',
  border: '1px solid rgba(74, 124, 89, 0.2)',
  marginTop: '8px',
  overflow: 'hidden',
};

export const organicNatureSelectOption: CSSProperties = {
  padding: '14px 18px',
  cursor: 'pointer',
  transition: 'var(--org-transition-fast)',
  borderBottom: '1px solid rgba(74, 124, 89, 0.1)',
};

export const organicNatureSelectOptionHover: CSSProperties = {
  background: 'rgba(74, 124, 89, 0.1)',
  color: 'var(--org-primary-dark)',
};

export const organicNatureSelectOptionSelected: CSSProperties = {
  background: 'var(--org-primary)',
  color: '#ffffff',
};

// ============================================
// TABLE STYLES
// ============================================

export const organicNatureTable: CSSProperties = {
  background: 'var(--org-bg-card)',
  borderRadius: 'var(--org-radius-lg)',
  boxShadow: 'var(--org-shadow)',
  overflow: 'hidden',
  border: '1px solid rgba(74, 124, 89, 0.1)',
};

export const organicNatureTableHeader: CSSProperties = {
  background: 'var(--org-primary)',
  color: '#ffffff',
  padding: '16px 20px',
  fontWeight: 'var(--org-font-weight-bold)' as unknown as number,
  textAlign: 'left',
};

export const organicNatureTableRow: CSSProperties = {
  borderBottom: '1px solid rgba(74, 124, 89, 0.1)',
  transition: 'var(--org-transition-fast)',
};

export const organicNatureTableRowHover: CSSProperties = {
  background: 'rgba(74, 124, 89, 0.05)',
};

export const organicNatureTableRowStriped: CSSProperties = {
  background: 'rgba(74, 124, 89, 0.03)',
};

export const organicNatureTableCell: CSSProperties = {
  padding: '16px 20px',
  color: 'var(--org-text)',
};

// ============================================
// MODAL STYLES
// ============================================

export const organicNatureModalOverlay: CSSProperties = {
  background: 'rgba(61, 61, 61, 0.6)',
  backdropFilter: 'blur(4px)',
};

export const organicNatureModal: CSSProperties = {
  background: 'var(--org-bg-card)',
  borderRadius: 'var(--org-radius-xl)',
  boxShadow: 'var(--org-shadow-lg)',
  padding: 'var(--org-spacing-xl)',
  border: '1px solid rgba(74, 124, 89, 0.15)',
};

export const organicNatureModalHeader: CSSProperties = {
  borderBottom: '1px solid rgba(74, 124, 89, 0.15)',
  paddingBottom: 'var(--org-spacing-md)',
  marginBottom: 'var(--org-spacing-lg)',
};

export const organicNatureModalTitle: CSSProperties = {
  fontFamily: 'var(--org-font-display)',
  fontSize: '1.5rem',
  color: 'var(--org-primary-dark)',
};

// ============================================
// TABS STYLES
// ============================================

export const organicNatureTabs: CSSProperties = {
  display: 'flex',
  background: 'var(--org-bg-secondary)',
  borderRadius: 'var(--org-radius)',
  padding: '6px',
  gap: '6px',
};

export const organicNatureTab: CSSProperties = {
  padding: '12px 24px',
  background: 'transparent',
  color: 'var(--org-text-secondary)',
  border: 'none',
  borderRadius: 'var(--org-radius-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--org-font)',
  fontWeight: 500,
  transition: 'var(--org-transition)',
};

export const organicNatureTabActive: CSSProperties = {
  background: 'var(--org-bg-card)',
  color: 'var(--org-primary)',
  boxShadow: 'var(--org-shadow-sm)',
};

export const organicNatureTabHover: CSSProperties = {
  color: 'var(--org-primary)',
  background: 'rgba(74, 124, 89, 0.1)',
};

// ============================================
// BADGE STYLES
// ============================================

export const organicNatureBadge: CSSProperties = {
  background: 'var(--org-primary)',
  color: '#ffffff',
  borderRadius: '999px',
  padding: '6px 14px',
  fontSize: '13px',
  fontFamily: 'var(--org-font)',
  fontWeight: 600,
};

export const organicNatureBadgeSuccess: CSSProperties = {
  background: 'var(--org-success)',
  color: '#ffffff',
};

export const organicNatureBadgeWarning: CSSProperties = {
  background: 'var(--org-warning)',
  color: 'var(--org-text)',
};

export const organicNatureBadgeDanger: CSSProperties = {
  background: 'var(--org-danger)',
  color: '#ffffff',
};

export const organicNatureBadgeInfo: CSSProperties = {
  background: 'var(--org-info)',
  color: '#ffffff',
};

export const organicNatureBadgeOutline: CSSProperties = {
  background: 'transparent',
  color: 'var(--org-primary)',
  border: '2px solid var(--org-primary)',
};

// ============================================
// TOOLTIP STYLES
// ============================================

export const organicNatureTooltip: CSSProperties = {
  background: 'var(--org-primary-dark)',
  color: '#ffffff',
  borderRadius: 'var(--org-radius-sm)',
  padding: '10px 14px',
  fontSize: '13px',
  fontFamily: 'var(--org-font)',
  boxShadow: 'var(--org-shadow)',
};

// ============================================
// SPECIAL ORGANIC SHAPES
// ============================================

export const organicNatureBlob: CSSProperties = {
  borderRadius: 'var(--org-radius-organic)',
  transition: 'var(--org-transition-slow)',
};

export const organicNatureLeafDecoration: CSSProperties = {
  position: 'absolute',
  width: '40px',
  height: '40px',
  backgroundImage: 'var(--org-texture-leaf)',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  opacity: 0.3,
};

// ============================================
// PALETTE VARIANTS
// ============================================

// Forest palette (default)
export const organicNatureForest: CSSProperties = {
  '--org-primary': '#4a7c59',
  '--org-primary-light': '#6b9b7a',
  '--org-primary-dark': '#2d5a3d',
} as CSSProperties;

// Desert palette
export const organicNatureDesert: CSSProperties = {
  '--org-primary': '#c9a959',
  '--org-primary-light': '#dbc47a',
  '--org-primary-dark': '#a68b3d',
} as CSSProperties;

// Ocean palette
export const organicNatureOcean: CSSProperties = {
  '--org-primary': '#3d7a8c',
  '--org-primary-light': '#5a9aac',
  '--org-primary-dark': '#2d5a66',
} as CSSProperties;

// Meadow palette
export const organicNatureMeadow: CSSProperties = {
  '--org-primary': '#7cb342',
  '--org-primary-light': '#9ccc65',
  '--org-primary-dark': '#5a8a2d',
} as CSSProperties;

// ============================================
// ANIMATIONS
// ============================================

export const organicNatureWave: CSSProperties = {
  animation: 'var(--org-wave)',
};

export const organicNatureFloat: CSSProperties = {
  animation: 'var(--org-float)',
};

